import type { VerticalConfig } from '@selenos/leads';
import { z } from 'zod';

import { driversConversationConfig } from './conversation-config';
import {
  renderDriverNurtureProfileBody,
  renderDriverNurtureReminderBody,
  renderPostDeliveryFinalBody,
  renderPostDeliveryNudgeBody,
} from './nurture-bodies';
import { driversOpenerControl, driversOpenerIntent } from './opener-variants';

/**
 * Custom fields for rideshare driver leads.
 *
 * Every qualification boolean is `.optional()`, NOT `.default(false)`. A missing
 * key means "the driver has not yet answered this question" and the scoring
 * classifier (see `requiredFieldsPopulated` in `@selenos/scoring/classification`)
 * holds the lead in `nurturing` until the answer is captured. If we defaulted
 * to `false` the landing-page form and the WhatsApp agent would both coerce
 * "unchecked / unanswered" into "no" and the hard-disqualification rule on
 * `hasDriversLicense` would auto-disqualify leads with empty custom_fields.
 */
export const DriverFieldsSchema = z.object({
  /** Driver's self-reported city — free-form. Normalized to
   *  `uber_territory_id` server-side via `findZaTerritoryId`. */
  city: z.string().min(1).optional().describe('City the driver operates in'),
  /** Numeric Uber territory ID resolved from `city`. Populated by the
   *  lead-capture pipeline, NOT by the agent. Hero delivery adapter
   *  reads this and falls back to 0 if missing. */
  uber_territory_id: z
    .number()
    .int()
    .positive()
    .optional()
    .describe('Uber numeric territory id (resolved from `city`)'),
  hasDriversLicense: z
    .boolean()
    .optional()
    .describe("I have a valid driver's license"),
  hasPrDP: z
    .boolean()
    .optional()
    .describe('I have a Professional Driving Permit (PrDP)'),
  hasSmartphone: z.boolean().optional().describe('I have a smartphone'),
  hasInsurance: z.boolean().optional().describe('I have vehicle insurance'),
  hasCleanRecord: z
    .boolean()
    .optional()
    .describe('I have a clean driving record'),
  /**
   * Derived from the Instant-Form `already_uber_driver` string by
   * `normalizeFormAnswers` (below), or collected by the WhatsApp agent.
   * TRUE = the applicant already drives for Uber. This is the revenue
   * question: only NEW-to-Uber drivers generate the Hero referral payout
   * (~R1500/activation, see delivery.referralValue); existing drivers are
   * R0 structurally (uber-hero adapter: membership_type EXISTING_DRIVER).
   * Stamped at CAPTURE time so delivery/nudge segmentation can prioritize
   * revenue-eligible leads without waiting for Uber's post-delivery
   * membership_type classification (PR #361).
   */
  alreadyUberDriver: z
    .boolean()
    .optional()
    .describe('I already drive for Uber (existing driver, not a new signup)'),
  /**
   * Work-eligibility (SA ID, or foreign passport + valid work permit) is
   * a practical Uber SA onboarding requirement (grade-C: secondary sources;
   * not listed on Uber's official documents page). Collected by the WhatsApp AGENT,
   * never on the Meta Instant Form: under Meta's Discriminatory Practices
   * policy (national origin is a protected attribute) a work-authorization
   * question on an EMPLOYMENT lead form is a MED-HIGH discrimination-flag
   * risk (not an explicit enumerated ban, but the strongest inferential
   * hook), so it lives in the consent-covered conversation instead. Risk
   * assessment: docs/investigations/uber-sa-driver-requirements/.
   */
  hasSaIdOrWorkPermit: z
    .boolean()
    .optional()
    .describe('I have an SA ID, or a passport with a valid work permit'),
  /**
   * Derived from the Instant-Form `has_vehicle` string by `normalizeFormAnswers`
   * (below). A driver with car access is a stronger Uber prospect, so it carries
   * scoring weight. Boolean so the standard `eq:true` scoring operator applies —
   * the raw `has_vehicle` string is kept alongside for the record.
   */
  hasVehicle: z
    .boolean()
    .optional()
    .describe('I have access to a vehicle for Uber'),
  /**
   * Raw answers from the Meta Instant Form (Drivers_SA_HigherIntent_Jun2026,
   * campaign 6954538512197). The webhook maps each form field name straight to
   * a customFields key, so these MUST match the form question keys exactly
   * (snake_case) or the leadgen Zod gate rejects the lead (unrecognized_keys ->
   * 500) and the paid lead never ingests. Free-text because the form options are
   * strings ("Yes, I own one" / "No, but I can get one" / ...), not booleans.
   */
  has_vehicle: z
    .string()
    .optional()
    .describe('Instant-Form: access to a car for Uber'),
  drivers_license: z
    .string()
    .optional()
    .describe("Instant-Form: valid SA driver's licence"),

  /**
   * Raw answer from the Meta Instant Form "Do you already drive with Uber?"
   * question (Drivers_SA_Eligibility_Jul2026 form). Free-text because the
   * form options are strings ("No, I'd be new to Uber" / "Yes, I already
   * drive with Uber"); `normalizeFormAnswers` derives the boolean
   * `alreadyUberDriver` and keeps this raw string for the record.
   */
  already_uber_driver: z
    .string()
    .optional()
    .describe('Instant-Form: already drives with Uber (raw answer)'),
});

export type DriverFields = z.infer<typeof DriverFieldsSchema>;

/**
 * Default vertical configuration for rideshare driver lead generation.
 *
 * Targets South African market, POPIA compliant, sourced via Meta Ads.
 * Outreach through email, SMS, and WhatsApp.
 */
export const driversConfig: VerticalConfig = {
  id: 'a1000000-0000-0000-0000-000000000005',
  name: 'Rideshare Drivers',
  slug: 'drivers',
  adCampaignPatterns: ['drivers_sa', 'drivers'],
  country: 'ZA',
  language: 'en',
  capture: {
    // The Selenos default primitive (ADR 0002): native CTWA -> Rovvo WABA
    // session (+16626414560) -> SA Driver Recruitment agent qualifies in-chat.
    // The Meta Lead Ads form engine (1,017 leads/90d @ ~CHF 0.54) was DELETED
    // 2026-06-06 on operator decision (agent capture only); its full spec is
    // archived in WIP_investigate_spitex_supply_ctwa_zero_leads_2026_06_06.md.
    primitive: 'whatsapp_agent',
  },

  customFieldsSchema: DriverFieldsSchema,
  conversationConfig: driversConversationConfig,

  /**
   * Map the raw Meta Instant-Form answer STRINGS onto the boolean scoring
   * fields, so a form lead actually scores instead of landing at 0.
   *
   * The webhook passes each Meta form field name straight into customFields, so
   * a drivers form lead arrives with `drivers_license` / `has_vehicle` as
   * STRINGS ("Yes" / "Yes, I own one" / "No, but I can get one"). The scoring
   * rules read the BOOLEANS `hasDriversLicense` / `hasVehicle`. Without this
   * mapping the booleans are absent → every rule misses → score 0 (the live
   * incident: all 102 leads scored 0). We derive the booleans here and KEEP the
   * raw strings for the record. Runs BEFORE the `.strict()` capture gate, so the
   * derived keys (already in DriverFieldsSchema) validate cleanly.
   *
   * Idempotent: re-running on already-normalized fields is a no-op. Only sets a
   * boolean when the corresponding raw string is present (a missing answer stays
   * "unanswered", not coerced to false — see the DriverFieldsSchema comment).
   */
  normalizeFormAnswers: (customFields: Record<string, unknown>) => {
    const out = { ...customFields };

    // "Yes" / "Yes, I own one" / "Yes, I can rent/lease one" → true.
    // "No" / "No, but I can get one" → false (a "no" is an answer, not absence).
    const yesNo = (raw: unknown): boolean | undefined => {
      if (typeof raw !== 'string') return undefined;
      const v = raw.trim().toLowerCase();
      if (v.length === 0) return undefined;
      return v.startsWith('yes');
    };

    const license = yesNo(out.drivers_license);
    if (license !== undefined && out.hasDriversLicense === undefined) {
      out.hasDriversLicense = license;
    }

    const vehicle = yesNo(out.has_vehicle);
    if (vehicle !== undefined && out.hasVehicle === undefined) {
      out.hasVehicle = vehicle;
    }

    // Insurance: the OTHER half of the delivery prerequisite. Without this
    // derivation the user answers a question that lands in a null boolean —
    // the exact shape of the bug this is being added to fix.
    // Insurance rides on the VEHICLE answer rather than its own question: the
    // form is capped at 2-4 questions on conversion research and already sits
    // at 4, so a fifth would have to displace one that earns its place.
    //
    //   "Yes, I own one and it's insured"  -> vehicle true,  insured true
    //   "Yes, I own one, not insured yet"  -> vehicle true,  insured FALSE
    //   "Yes, I can rent/lease one"        -> vehicle true,  insured UNKNOWN
    //   "No"                               -> vehicle false, insured unknown
    //
    // Rent/lease stays unknown deliberately. A fleet car usually comes insured
    // and assuming so is exactly the guess this whole change exists to remove;
    // that cohort is no worse off than today.
    const rawVehicle =
      typeof out.has_vehicle === 'string' ? out.has_vehicle.toLowerCase() : '';
    if (out.hasInsurance === undefined && rawVehicle.startsWith('yes')) {
      if (
        rawVehicle.includes("it's insured") ||
        rawVehicle.includes('and insured')
      ) {
        out.hasInsurance = true;
      } else if (rawVehicle.includes('not insured')) {
        out.hasInsurance = false;
      }
    }

    // "Yes, I already drive with Uber" → true (existing driver, R0 referral);
    // "No, I'd be new to Uber" → false (revenue-eligible NEW driver).
    const alreadyDriver = yesNo(out.already_uber_driver);
    if (alreadyDriver !== undefined && out.alreadyUberDriver === undefined) {
      out.alreadyUberDriver = alreadyDriver;
    }

    return out;
  },

  enrichment: {
    providers: ['clay'],
    primaryProvider: 'clay',
    fallbackProviders: [],
    skipEnrichment: true,
  },

  scoring: {
    rules: [
      {
        field: 'hasDriversLicense',
        operator: 'eq',
        value: true,
        points: 30,
        required: true,
        description: 'Must have a valid drivers license',
      },
      {
        field: 'hasPrDP',
        operator: 'eq',
        value: true,
        points: 20,
        description:
          'Professional Driving Permit (PrDP) required in South Africa',
      },
      {
        field: 'hasSmartphone',
        operator: 'eq',
        value: true,
        points: 10,
        description: 'Smartphone required for the rideshare app',
      },
      {
        field: 'hasInsurance',
        operator: 'eq',
        value: true,
        points: 10,
        description: 'Active insurance policy',
      },
      {
        field: 'hasCleanRecord',
        operator: 'eq',
        value: true,
        points: 20,
        description: 'Clean driving record (high weight for safety)',
      },
      {
        field: 'hasVehicle',
        operator: 'eq',
        value: true,
        points: 15,
        description: 'Has access to a vehicle for Uber (driver-supplied car)',
      },
      {
        // THE revenue question (2026-07-06). Only NEW-to-Uber drivers generate
        // the Hero referral payout (~R1500/activation); existing drivers are
        // structurally R0 (uber-hero adapter classifies them EXISTING_DRIVER
        // and skips CAPI/convert, PR #275/#361). eq:false so the points land
        // on the revenue-eligible answer. Weighting is deliberate: a fully
        // answered NEW-driver Instant-Form lead (license 30 + vehicle 15 +
        // phone 5 + this 10) exactly clears the qualified threshold (60),
        // while an existing driver stays at 50 (nurture): capture-time
        // routing instead of waiting for Uber's post-delivery classification.
        field: 'alreadyUberDriver',
        operator: 'eq',
        value: false,
        points: 10,
        description:
          'New to Uber (revenue-eligible Hero referral; existing drivers deprioritized)',
      },
      {
        // Work-eligibility (SA ID or passport + valid work permit) is an Uber
        // SA onboarding requirement, collected by the WhatsApp agent only
        // (never on the Meta form; Meta prohibits citizenship/immigration
        // questions in lead forms). Small weight: it confirms eligibility but
        // is not a stronger signal than the document questions above.
        field: 'hasSaIdOrWorkPermit',
        operator: 'eq',
        value: true,
        points: 5,
        description:
          'Work eligibility confirmed (SA ID, or passport + valid work permit)',
      },
      {
        // Resolved from base lead fields, not custom DriverFieldsSchema
        field: 'phone',
        operator: 'exists',
        value: true,
        points: 5,
        description: 'Phone number provided signals intent',
      },
    ],
    aiPromptTemplate:
      'Evaluate this potential rideshare driver lead. Consider their driving eligibility, document readiness, and motivation. Score from 0-100.',
    thresholds: {
      qualified: 60,
      nurture: 40,
      discard: 20,
    },
  },

  outreach: {
    // Consent text in conversation-config.ts is "contacted about your driver
    // application" — purpose-scoped, not channel-scoped. Per operator policy
    // 2026-04-30: lead consent applies to any channel we may use to reach
    // them about job opportunities (POPIA §11(1)(a) requires consent be
    // *specific to purpose*, not specific to channel). enabledChannels here
    // therefore matches the full set of channels recruitment sequences may
    // attempt — including sms and voice — so consent_records get written
    // up-front and the compliance gate doesn't silently block legitimate
    // outreach.
    enabledChannels: ['email', 'whatsapp', 'sms', 'phone'],
    // WhatsApp sends leave from the drivers ChatSuite session. Switched
    // 2026-06-30 from the Meta Cloud-API WABA (+16626414560, WABA
    // 867370819695463) to the Baileys "Rovvo SIM" session (+41765022800,
    // org 5) — the Akson BM + that WABA are permanently banned, so all
    // outreach now runs through ChatSuite Baileys. ChatSuite picks the
    // transport from the session that owns this tel (Baileys here). The
    // one-shot first message is already free-form Baileys body.
    //
    // `transport: 'baileys'` is the sender gateway's provider default for this
    // number until a `waba_numbers` registry row exists (the seed migration).
    // Without it, an empty registry would default the number to `meta_cloud_api`
    // and re-break Issue 9 (a template over Baileys) in the pre-seed window.
    chatsuiteSession: { tel: '+41764776713', transport: 'baileys' },
    // Inbound-email Phase 2: the drivers email channel is handled by the same
    // channel-agnostic ChatSuite agent that qualifies drivers on WhatsApp
    // ("SA Driver Recruitment Agent", ChatSuite org 5). Forwarding is still
    // gated off by default via operational_flags (agent_email_enabled:drivers
    // + agent_email_kill_switch); flip the flag to pilot.
    emailAgent: {
      organizationId: '5',
      agentId: '7fc72d8a-3bf8-4db0-a35e-9b554460e561',
    },
    // Reactive handoff prefill: what the DRIVER sends us when they tap the CTA
    // on the Instant Form thank-you screen (`/wa/drivers` → wa.me). Written in
    // their voice, not ours. Making the lead open the thread is the single
    // biggest ban-rate lever we have (reactive <2%/yr vs proactive 15-30%/yr);
    // the `?text=` prefill itself is worth 20-30% of replies (`checkCtaUrl`).
    handoffPrefill: "Hi, I've applied to drive and I'd like to get set up.",
    // One-shot first message (Lead Ads → WhatsApp/Baileys, fired within ~60s of
    // capture). Free-form Baileys text — no template, no WABA, no buttons. Copy
    // from docs/investigations/lead-first-message/2026-06-25-one-shot-first-message-design.md:
    // name+city personalized, R100-150/hr earnings anchor (the V1-winner copy),
    // ONE benefit-framed question (PrDP — the #1 SA dropout gate), named-team
    // trust signal to counter scam-skepticism. Reply hands into the AI agent.
    firstMessage: {
      transport: 'baileys',
      // SERVICE-scope: a transactional reply to the driver's own Lead Ads form
      // submission (acknowledge + one onboarding question). Meta Lead Ads leads
      // grant `whatsapp_service` consent only (no marketing opt-in on the form),
      // so a MARKETING-framed one-shot is blocked by Rail 6. Keep the body free
      // of earnings/rate claims (the gateway's category-at-send guard blocks a
      // service send containing marketing lexicon). The AI agent discusses
      // earnings once the driver replies — that is a service-initiated
      // conversation, not an outbound marketing push.
      category: 'service',
      // REACTIVE-FIRST: hold the opener for two hours. The Instant Form's
      // thank-you CTA hands the driver into WhatsApp with a pre-filled message
      // (`/wa/drivers`), so the intended first message is THEIRS. Firing ours
      // ~60s after capture raced that handoff and made the funnel proactive in
      // practice whatever the ad said. If they write inside the window,
      // `leads.first_inbound_at` is stamped and this job skips; if they stay
      // silent, they still get exactly one opener, two hours later.
      delayMinutes: 120,
      // Control body — the live opener (name + city personalized, PrDP question
      // with concrete options). Also the fallback whenever the experiment below
      // is disabled or resolves to no arm. City placeholder handling ("Other")
      // lives in resolveNameCity; see opener-variants.ts.
      body: driversOpenerControl,
      // Opener A/B (STAGED, disabled). Flip `enabled: true` + merge to start the
      // 50/50 control-vs-intent test. Assignment is deterministic per lead UUID
      // (pickWeightedVariant); the assigned arm is stamped into
      // outreach_log.metadata.opener_variant for reply-rate splitting. Baseline
      // control reply rate ~76% (2026-06-28→07-05). Spec + read query:
      // docs/wip/active/WIP_fixing_drivers_opener_yield_2026_07_05.md.
      experiment: {
        key: 'drivers_opener_2026_07',
        enabled: false,
        variants: {
          control: { weight: 1, body: driversOpenerControl },
          intent: { weight: 1, body: driversOpenerIntent },
        },
      },
    },
    sequences: [
      {
        id: 'rideshare-welcome',
        name: 'Driver Welcome (one WhatsApp + email fallback)',
        // NEVER-RESTART (2026-06-25): exactly ONE business-initiated WhatsApp
        // (step 0, the one-shot first message fired by the hot-reply lane). All
        // follow-ups to a non-replier are EMAIL — re-sending WhatsApp to a
        // silent lead is the #1 report/ban driver (the 2026-06 WABA ban). The
        // old multi-WhatsApp steps (reminder/followup/urgency/final) are removed.
        // (SMS/voice fallback is a future add — no SMS/phone templates exist yet.)
        steps: [
          // Step 0: the one WhatsApp — first message (fired via hot-reply lane).
          {
            channel: 'whatsapp',
            delayMinutes: 0,
            templateId: 'rideshare-driver-welcome-whatsapp',
          },
          // Step 1: Email detailed welcome.
          {
            channel: 'email',
            delayMinutes: 0,
            templateId: 'rideshare-welcome-email',
          },
          // Step 2: Email earnings content (3 days) — non-replier follow-up.
          {
            channel: 'email',
            delayMinutes: 4320,
            templateId: 'rideshare-application-incomplete-email',
            condition: 'not_engaged',
          },
        ],
      },
      {
        id: 'rideshare-welcome-compressed',
        name: 'Compressed Welcome (one WhatsApp + email)',
        // NEVER-RESTART: one WhatsApp, email fallback. Old 3-WhatsApp cadence removed.
        steps: [
          {
            channel: 'whatsapp',
            delayMinutes: 0,
            templateId: 'rideshare-driver-welcome-whatsapp',
          },
          {
            channel: 'email',
            delayMinutes: 1440,
            templateId: 'rideshare-welcome-email',
            condition: 'not_engaged',
          },
        ],
      },
      {
        // F3e post-delivery Uber-completion nurture.
        //
        // Triggers on lead.status transition TO 'delivered' (i.e. after
        // the Uber Supplier Portal accepted the driver and Uber has
        // sent the signup invite SMS/email). 158 of 160 invited drivers
        // are stuck at INVITE_STATUS_PENDING (2026-04-20 F3 RCA): the
        // Uber signup click-through is the real conversion bottleneck,
        // and nothing in the platform currently reminds drivers to
        // finish after we deliver.
        //
        // Dispatched from `packages/features/delivery/src/delivery-engine.ts`
        // via `enqueueOnDeliveredSequences()` immediately after
        // `transitionToDelivered()` succeeds. The handler respects each
        // step's `delayMinutes`, so step 0 fires at +24h, not on delivery.
        // Idempotency key `outreach-ondelivered-<leadId>-<seqId>-0` prevents
        // double-fire on re-delivery.
        //
        // Cadence: 3 touches over a week. Aggressive enough to recover
        // fence-sitters, gentle enough that a driver who declined is
        // not spammed. `not_engaged` gates touch 2 and 3 so a driver
        // who replies (e.g. "done!") exits early.
        id: 'rideshare-post-delivery',
        name: 'Post-delivery Uber-completion Nurture',
        triggerOn: 'delivered',
        steps: [
          // Day +1 (24h post-delivery) — WhatsApp nudge. Free-form Baileys body
          // (SERVICE): the drivers session is Baileys-only and cannot send a
          // Meta template. `templateId` is retained for outreach_log attribution
          // + sequence idempotency; `body` is what actually delivers.
          {
            channel: 'whatsapp',
            delayMinutes: 1440,
            templateId: 'rideshare-post-delivery-nudge-whatsapp',
            body: renderPostDeliveryNudgeBody,
          },
          // Day +3 — Email with proof + value prop.
          {
            channel: 'email',
            delayMinutes: 4320,
            templateId: 'rideshare-post-delivery-earnings-email',
            condition: 'not_engaged',
          },
          // Day +7 — Final WhatsApp nudge (free-form Baileys body). After this
          // the lead stays `delivered` and no further outreach fires from this
          // sequence.
          {
            channel: 'whatsapp',
            delayMinutes: 10080,
            templateId: 'rideshare-post-delivery-final-whatsapp',
            condition: 'not_engaged',
            body: renderPostDeliveryFinalBody,
          },
        ],
      },
      {
        id: 'driver-nurture',
        name: 'Driver Profile Completion Nurture',
        steps: [
          // Free-form Baileys body (SERVICE): drivers is Baileys-only, so the
          // Meta-template send failed 100%. `templateId` kept for attribution +
          // idempotency; `body` delivers.
          //
          // 180 min, NOT 60: it must land AFTER the opener's 120-minute
          // reactive-first hold. At 60 it became the first message a nurture-band
          // lead ever received, and the opener that followed at 120 was then
          // blocked by never-restart (`everSent && !everReplied`). A
          // profile-completion reminder is the wrong thing to open a
          // relationship with; the opener names them, their city and the PrDP
          // question.
          {
            channel: 'whatsapp',
            delayMinutes: 180,
            templateId: 'driver-nurture-profile-whatsapp',
            body: renderDriverNurtureProfileBody,
          },
          {
            channel: 'email',
            delayMinutes: 1440,
            templateId: 'driver-nurture-profile-email',
            condition: 'not_engaged',
          },
          {
            channel: 'whatsapp',
            delayMinutes: 4320,
            templateId: 'driver-nurture-reminder-whatsapp',
            condition: 'not_engaged',
            body: renderDriverNurtureReminderBody,
          },
          {
            channel: 'email',
            delayMinutes: 10080,
            templateId: 'driver-nurture-reengagement-email',
            condition: 'not_engaged',
          },
        ],
      },
      // Uber Hero invite->ACTIVE conversion nudge (2026-06-15 RCA). Fired by
      // the uber-segment-nudge cron's Hero step for drivers stuck at a
      // pre-ACTIVE onboarding status (APPLIED/LINKED) after a valid Hero
      // invite. Single touch this round; further cadence is a human sign-off
      // item (see WIP_hero_invite_active_nudge_2026_06_16.md). The template is
      // seeded as `draft`, so this step cannot deliver until a human submits
      // it and Meta approves (the send path fail-closes on a missing
      // meta_template_id).
      {
        id: 'rideshare_hero_finish_signup_en',
        name: 'Hero finish-signup nudge (EN)',
        steps: [
          {
            channel: 'whatsapp',
            delayMinutes: 0,
            templateId: 'rideshare-hero-finish-signup-whatsapp-en',
          },
        ],
      },
      // Single final-touch fired by sweep-stale-leads (triggerOn: 'stale')
      // before a stale lead is terminalized. EMAIL, not WhatsApp: re-opening a
      // dormant conversation with a fresh WhatsApp is a "restart" — the exact
      // report/ban risk the never-restart rule forbids (2026-06-25). Email has
      // no such exposure.
      {
        id: 'rideshare-reactivation',
        name: 'Stale-lead final re-activation touch (email)',
        triggerOn: 'stale',
        steps: [
          {
            channel: 'email',
            delayMinutes: 0,
            templateId: 'rideshare-application-incomplete-email',
          },
        ],
      },
      // Backlog recovery: the ONE WhatsApp opener for leads captured during a
      // transport outage that never received it. No `triggerOn` — nothing
      // fires it from a lead event.
      //
      // It used to say "fired only by the drivers-backlog-recovery cron". That
      // was wrong even before the cron was deleted: `deferStep` in
      // job-handlers.ts re-enqueues OutreachExecuteStep with the SAME
      // sequenceId on every deferral, uncapped by design, and that loop
      // produced 86158 of this sequence's 89205 job rows against the cron's
      // 1265. The cron was 1.4% of it.
      //
      // Why a separate sequence rather than re-firing `rideshare-welcome`
      // step 0: the handler's idempotency treats ANY non-`pending` outreach_log
      // row as "step completed", so the `failed` / `skipped` rows these leads
      // already carry would make a re-fire write another `skipped` row and send
      // nothing. `completedStepIndexes` is scoped by `metadata->>sequenceId`,
      // so a distinct sequence id is the sanctioned way to give an
      // already-attempted lead a genuine first delivery.
      //
      // NOT a never-restart violation: that rail blocks on a prior *successful*
      // send with no reply (`isNeverRestartBlocked` = everSent && !everReplied),
      // and the runner only selects leads with no `sent`/`replied` WhatsApp row.
      // For these drivers this is the first WhatsApp that ever reaches them.
      // `body` (not `templateId`) delivers: drivers is Baileys-only and Baileys
      // cannot send Meta templates.
      {
        // STILL LIVE as of 2026-08-22, despite the cron being deleted. Do not
        // read the deletion as a stop.
        //
        // Enqueuers that remain:
        //   1. `deferStep` (job-handlers.ts) — re-enqueues with this same
        //      sequenceId on every deferral, hops "deliberately uncapped".
        //      86158 of this sequence's 89205 job rows. THIS is the lane that
        //      sent 5 cold opens on 2026-08-21, eight days after the
        //      restriction, three of which real drivers answered.
        //   2. `/api/cron/outreach-retry-failed` — rebuilds a payload from
        //      `outreach_log.metadata.sequenceId` verbatim and re-enqueues.
        //      630 `jobs_dead` rows for this sequence are replayable.
        //
        // 51 pending jobs across 25 leads are in the queue right now, held ONLY
        // by warmup while the number is quarantined. They clear the moment
        // `waba_numbers.status` goes back to `active`, and then they send.
        // Cancel them first:
        // `apps/web/scripts/cancel-drivers-welcome-sequence-jobs.ts --dry-run`.
        //
        // The definition stays because it has leads mid-flight and a live
        // replay path, so it is not unused infrastructure today. Revisit
        // removal once the queue is drained. Read
        // docs/wip/refs/baileys-number-burn-hypotheses.md before re-enabling
        // anything here.
        id: 'rideshare-backlog-recovery',
        name: 'Outage backlog recovery (one WhatsApp opener)',
        steps: [
          {
            channel: 'whatsapp',
            delayMinutes: 0,
            templateId: 'rideshare-driver-welcome-whatsapp',
            body: driversOpenerControl,
            // UTILITY, not the ID heuristic's MARKETING ("welcome" substring):
            // this is the same SERVICE-scope transactional opener the hot-reply
            // lane sends (see firstMessage.category above — a reply to the
            // driver's own Lead Ads submission). The heuristic's MARKETING
            // reading made Rail 6 demand `whatsapp_marketing` consent no Meta
            // Lead Ads lead has, blocking this lane 100% from its first day
            // (prod-verified 2026-08-10: 0 sent rows ever, 56/56 skipped).
            templateCategory: 'UTILITY',
          },
        ],
      },
      // under_qualified field-collection touch. Enqueued directly (step 0) by
      // the delivery-retry-stalled sweep for leads whose delivery soft-blocked
      // as `under_qualified` and that have no live WhatsApp conversation still
      // collecting fields. The email links to the signed `/complete-profile`
      // form that collects exactly the missing qualification-gate answers; on
      // submit the lead is re-scored + re-enqueued for delivery. Single email
      // step (no WhatsApp — never-restart) with no `triggerOn`, so it is only
      // ever fired explicitly by the sweep, capped at 2 touches per lead.
      {
        id: 'rideshare-under-qualified-collection',
        name: 'Under-qualified field collection (email)',
        steps: [
          {
            channel: 'email',
            delayMinutes: 0,
            templateId: 'rideshare-collect-fields-email',
          },
        ],
      },
    ],
    rateLimits: [
      { channel: 'email', maxPerHour: 100, maxPerDay: 500 },
      { channel: 'whatsapp', maxPerHour: 30, maxPerDay: 150 },
    ],
    experiment: {
      enabled: true,
      allocation: {
        'rideshare-welcome': 0.5,
        'rideshare-welcome-compressed': 0.5,
      },
    },
  },

  compliance: {
    jurisdiction: 'za-popia',
    consentRequirements: [
      'Explicit consent for processing personal information under POPIA',
      'Clear opt-in checkbox (not pre-checked)',
      'Privacy policy link visible on form',
      'Purpose of data collection clearly stated',
      'Right to withdraw consent communicated',
    ],
    dncEnabled: true,
  },

  delivery: {
    // Hero is the sole Uber integration: the active referral-commission
    // channel since 2026-05-19. The legacy `uber_supplier` fleet adapter was
    // fully retired 2026-07-05 (0 conversions, status sync dead since
    // 2026-04-28). Verdict + removal inventory:
    //   docs/investigations/uber-supplier-channel-verdict/findings-2026-07-05.md
    adapter: 'uber_hero',
    format: 'json',
    // F3d — typical Uber SA per-activation referral payout (ZAR).
    // Threaded into the CAPI `Purchase` event by the uber-hero-status-sync /
    // uber-hero-reward-sync crons so Meta ad optimisation learns the revenue
    // weight per convert. Adjust when Uber's payout structure changes.
    referralValue: 1500,
    // Qualification-completeness gate (delivery engine, pre-adapter). Stops
    // empty Meta Lead Ads leads — which reach `qualified` via an AI-manufactured
    // score with no real answers — from auto-delivering to the Uber Hero
    // portal. A lead must have answered at least 3 of these 7 qualification
    // fields. WhatsApp-agent leads (which collect the full conversational set)
    // clear this easily; empty form leads do not, and stay non-terminal until
    // re-engagement collects the answers. `false` (e.g. "no PrDP") counts as an
    // answer — the gate measures completeness, scoring measures positivity.
    //
    // `hasVehicle` is a gate field (added 2026-06-17). It is a genuine Uber
    // qualifier (a driver needs car access; it carries 15 scoring points) AND
    // the only delivery-relevant field the 3-question Instant Form collects
    // beyond `city` + `hasDriversLicense`. Before this, a fully-answered Instant
    // Form populated only 2 of the gate fields (city + hasDriversLicense — the
    // `has_vehicle` string normalizes to `hasVehicle`, which was NOT a gate
    // field), so EVERY qualified Instant-Form lead failed `minPopulatedFields:3`
    // → soft-blocked `under_qualified` → never delivered (the 4%-vs-40% delivery
    // gap vs conversational CTWA leads). Counting `hasVehicle` makes the form's
    // three real answers (city, licence, vehicle) clear the gate WITHOUT lowering
    // the bar: it requires three honest qualification answers, just three the
    // form can actually collect. A Meta HIGHER_INTENT form is capped at 2-3
    // questions, so a gate that demanded 3 of 6 fields a 3-question form never
    // asks was structurally unsatisfiable; the deploy-time gate-satisfiability
    // guard (scripts/lib/instant-form-key-schema-guard.ts) now fails closed on
    // that mismatch before spend.
    qualificationGate: {
      fields: [
        'hasDriversLicense',
        'hasPrDP',
        'hasSmartphone',
        'hasInsurance',
        'hasCleanRecord',
        'hasVehicle',
        'city',
      ],
      minPopulatedFields: 3,
      // Delivery prerequisites (added 2026-07-06, digest-audit finding:
      // docs/investigations/agent-improvement-digest-audit/findings-2026-07-06.md).
      // Scoring hard-requires only hasDriversLicense; hasVehicle (+15) and
      // hasInsurance (+10) are soft points, so a no-vehicle/no-insurance
      // applicant clears the 60-point threshold and used to be DELIVERED to
      // Uber unqualifiable (a direct feeder of the ~80% under_qualified
      // dropout on Uber's side). Both must now be explicitly true before the
      // uber_hero adapter runs. NOT a capture/qualification change: the lead
      // is always saved and scored ("disqualification is a status, never
      // blocks creation"); delivery soft-blocks `under_qualified` so the
      // field-collection re-processor (WS-C, PR #317) collects missing
      // answers. Confirmed "no" answers stamp `custom_fields.vehicle_gap`
      // ('no_vehicle' | 'no_insurance' | 'both'): that cohort is future
      // SUPPLY for fleet-partner buyers (rent-to-own puts carless drivers in
      // insured vehicles; docs/strategy/selenos-prospect-list-2026-07-06.md
      // segment 2). No outreach change: tagging only.
      prerequisites: {
        fields: ['hasVehicle', 'hasInsurance'],
        tagField: 'vehicle_gap',
        tagValueByField: {
          hasVehicle: 'no_vehicle',
          hasInsurance: 'no_insurance',
        },
        tagValueAll: 'both',
      },
    },
  },

  theme: {
    primaryColor: '#06C167',
    secondaryColor: '#000000',
    accentColor: '#F5A623',
    heroStyle: 'dark',
    personality: 'bold',
  },

  branding: {
    brandName: 'Rovvo',
    // Assets live in public/branding/rideshare-drivers/ (the vertical slug is
    // 'drivers' but the asset dir was named after the vertical's full name).
    // The config previously pointed at /branding/drivers/ which does not exist
    // → broken favicon/apple-touch-icon/logo on the drivers landing pages.
    faviconIco: '/branding/rideshare-drivers/favicon.ico',
    appleTouchIcon: '/branding/rideshare-drivers/apple-touch-icon.png',
    logo: '/branding/rideshare-drivers/logo.png',
    themeColor: '#06C167',
    // We help drivers sign up WITH Uber and Bolt; we are not either of them.
    // Naming them as a destination is correct and expected. Claiming to BE them
    // is what the live opener did ("this is the Uber Hero team") when this
    // number was restricted under Commerce Policy s.1.
    disclaimsAffiliationWith: ['Uber', 'Bolt'],
  },

  email: {
    fromAddress: 'drivers@notify.akson.ch',
    fromName: 'Rovvo',
    // The from-domain `notify.akson.ch` is a Resend send-only subdomain (no
    // inbound MX) — replies to it bounce. Route replies to the consolidated
    // Resend Inbound address so the `resend-inbound` webhook captures them,
    // links them to the lead, and surfaces them (Slack). One inbound domain,
    // one local part per vertical, so a reply from an unknown sender is still
    // attributed to this vertical's account.
    replyToAddress: 'drivers@reply.selenos.ai',
    brandHomepageUrl: 'https://selenos.ai',
    applicationUrlTemplate: 'https://selenos.ai/drivers/{city}/apply',
    defaultCity: 'johannesburg',
    // Verified live 2026-07-19: these four return 200, everything else
    // (polokwane, gauteng, port-elizabeth) 404s. They also match the four
    // options on the Meta Instant Form exactly. Add a slug here only
    // after its landing page ships.
    supportedCities: ['johannesburg', 'cape-town', 'pretoria', 'durban'],
    privacyPolicyUrl: 'https://selenos.ai/privacy',
  },

  google: {
    conversionId: process.env.NEXT_PUBLIC_DRIVERS_GOOGLE_CONVERSION_ID,
    conversionLabel: process.env.NEXT_PUBLIC_DRIVERS_GOOGLE_CONVERSION_LABEL,
    qualificationConversionLabel:
      process.env.DRIVERS_GOOGLE_QUALIFICATION_LABEL,
    deliveryConversionLabel: process.env.DRIVERS_GOOGLE_DELIVERY_LABEL,
  },

  landingPage: {
    // WhatsApp CTA: set NEXT_PUBLIC_DRIVERS_WHATSAPP_NUMBER to the ChatSuite SA business number (E.164 without +, e.g. 27XXXXXXXXX)
    whatsappCtaUrl: process.env.NEXT_PUBLIC_DRIVERS_WHATSAPP_NUMBER
      ? `https://wa.me/${process.env.NEXT_PUBLIC_DRIVERS_WHATSAPP_NUMBER}?text=Hi%2C+I%27m+interested+in+driving+with+Uber+in+South+Africa`
      : undefined,
    theme: {
      primaryColor: '#000000',
      secondaryColor: '#276EF1',
      fontFamily: 'Inter, sans-serif',
    },
    copyOverrides: {
      headline: 'Drive with Uber. Your car, your schedule.',
      subheadline:
        'Join Rovvo. We guide you through every step of getting set up to drive.',
      ctaButton: 'Apply to drive',
      formTitle: 'Get Behind the Wheel with Rovvo',
    },
    trustSignals: [
      {
        type: 'stat',
        content: '6+ cities across South Africa',
      },
      {
        type: 'stat',
        content: 'Flexible hours, drive when it suits you',
      },
      {
        type: 'guarantee',
        content: 'Apply in under 5 minutes. Drive this week.',
      },
      {
        type: 'testimonial',
        key: 'sipho',
        content:
          'I signed up on Monday and had my first trip by Wednesday. The whole process was way simpler than I expected.',
        attribution: 'Sipho M., Driver, Johannesburg',
      },
      {
        type: 'testimonial',
        key: 'thandiwe',
        content:
          'I do the airport runs in Cape Town on weekends. Best side hustle I have had. The flexibility is unreal.',
        attribution: 'Thandiwe K., Driver, Cape Town',
      },
      {
        type: 'testimonial',
        key: 'david',
        content:
          'I drive mornings before my other job. Some weeks I clear R4,000 just from the morning rush. Nobody tells me when to start or stop.',
        attribution: 'David N., Part-time Driver, Pretoria',
      },
    ],
  },
};
